import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { createEntityAdapter, Dictionary, EntityState } from '@reduxjs/toolkit';

import createSafeContext from '#/utils/createSafeContext';
import { getOpenOrders, OpenOrdersResp, OpenOrder } from '#/api/orders';
import { usePerpetualMarketsState } from '#/features/perpetuals/market-info/markets-context';
import { OrderNotification } from '#/api/ws/ws';
import useLiveConnection, {
  LiveConnectionBaseState,
} from '#/utils/useLiveConnection';
import { Market } from '#/api/markets';
import { useWalletView } from '#/features/wallet/wallet-context';
import { TFunction, useTranslation } from '#/features/localization/utils';

import {
  clientIdView,
  createdTimeView,
  createdDateView,
  typeView,
  priceView,
  sizeView,
  filledSizeView,
  instructionView,
  OpenOrderView,
} from './order-view';

const adapter = createEntityAdapter<OpenOrder>({
  sortComparer: (orderA, orderB) =>
    orderB.created_at.getTime() - orderA.created_at.getTime(),
});
const selectors = adapter.getSelectors();

const INITIAL_STATE: OpenOrdersState = {
  status: 'connected',
  error: '',
  openOrders: adapter.getInitialState(),
};

export interface OpenOrdersState extends LiveConnectionBaseState {
  readonly openOrders: EntityState<OpenOrder>;
}
interface OpenOrdersView extends Omit<OpenOrdersState, 'openOrders'> {
  readonly openOrders: readonly OpenOrderView[];
  getOrders(): readonly OpenOrder[];
}

export const [useOpenOrdersView, OpenOrdersViewCtx] =
  createSafeContext<OpenOrdersView>('OpenOrdersView');

interface ProviderProps {
  readonly children: ReactNode;
}

export function OpenOrdersProvider(props: ProviderProps) {
  const [state, setState] = useState(INITIAL_STATE);
  const { markets } = usePerpetualMarketsState();
  const { t } = useTranslation();

  const view = useMemo(
    () => createView(state, markets.entities, t),
    [state, markets.entities, t],
  );

  useOpenOrdersFetch(setState);

  return <OpenOrdersViewCtx value={view}> {props.children}</OpenOrdersViewCtx>;
}

export function createView(
  state: OpenOrdersState,
  markets: Dictionary<Market>,
  _t: TFunction,
): OpenOrdersView {
  const openOrders = selectors
    .selectAll(state.openOrders)
    .map((order) => openOrderView(order, markets));

  return {
    ...state,
    openOrders,
    getOrders: () => selectors.selectAll(state.openOrders),
  };
}

function openOrderView(
  order: OpenOrder,
  markets: Dictionary<Market>,
): OpenOrderView {
  const market = markets[order.market];

  const clientId = clientIdView(order);
  const createdTime = createdTimeView(order);
  const createdDate = createdDateView(order);
  const type = typeView(order);
  const price = priceView(order, market);
  const size = sizeView(order, market);
  const filledSize = filledSizeView(order, market);
  const instruction = instructionView(order);

  return {
    id: order.id,
    clientId,
    createdTime,
    createdDate,
    market: order.market,
    type,
    side: order.side,
    price,
    size,
    filledSize,
    instruction,
  };
}

function useOpenOrdersFetch(
  setState: Dispatch<SetStateAction<OpenOrdersState>>,
): void {
  const walletView = useWalletView();

  const isReady = walletView.isSignedIn;

  const channel = isReady ? `orders.ALL` : '';

  const fetchAll = useCallback(
    async (signal: AbortSignal) =>
      getOpenOrders({
        signal,
      }),
    [],
  );

  const setAll = useCallback(
    (data: OpenOrdersResp) => {
      setState((state) => ({
        ...state,
        openOrders: adapter.setAll(state.openOrders, data.results),
      }));
    },
    [setState],
  );

  const resetAll = useCallback(() => {
    setState((state) => ({
      ...state,
      openOrders: adapter.removeAll(state.openOrders),
    }));
  }, [setState]);

  const applyWSNotification = useCallback(
    (notif: OrderNotification) => {
      setState((state) => {
        switch (notif.data.status) {
          case 'NEW':
          case 'OPEN':
            return {
              ...state,
              openOrders: adapter.setOne(
                state.openOrders,
                notif.data as OpenOrder,
              ),
            };
          case 'CLOSED':
            return {
              ...state,
              openOrders: adapter.removeOne(state.openOrders, notif.data.id),
            };

          // no default
        }
      });
    },
    [setState],
  );

  useLiveConnection<OpenOrdersResp, OpenOrdersState, OrderNotification>({
    setState,
    channel,
    isReady,
    fetchAll,
    setAll,
    resetAll,
    applyWSNotification,
  });
}
